import './template.css';

interface SegmentData {
  segment?: string;
  length?: number;
  diameter?: number;
  stenosis?: number;
  sliceRange?: string;
  SOPInstanceUID?: string;
  aiAssessment?: string;
}

interface UpperKnee {
  patientName?: string;
  age?: string;
  gender?: string;
  abdominalAorta?: SegmentData[];
  iliacR?: SegmentData[];
  iliacL?: SegmentData[];
  eiliacR?: SegmentData[];
  eiliacL?: SegmentData[];
  sfaR?: SegmentData[];
  sfaL?: SegmentData[];
}

interface RightLeg {
  poplitealR?: SegmentData[];
  tibioR?: SegmentData[];
  posteriorR?: SegmentData[];
  peronealR?: SegmentData[];
  anteriorR?: SegmentData[];
}

interface LeftLeg {
  poplitealL?: SegmentData[];
  tibioL?: SegmentData[];
  posteriorL?: SegmentData[];
  peronealL?: SegmentData[];
  anteriorL?: SegmentData[];
}

interface ReportData {
  upperKnee?: UpperKnee;
  rightLeg?: RightLeg;
  leftLeg?: LeftLeg;
}

const getAiReportBody = (reportData: ReportData, studyInstanceId?: string): string => {
  const upperKnee = reportData?.upperKnee ? reportData.upperKnee : ({} as UpperKnee);
  const leftLeg = reportData?.leftLeg ? reportData.leftLeg : ({} as LeftLeg);
  const rightLeg = reportData?.rightLeg ? reportData.rightLeg : ({} as RightLeg);

  const buildSliceRangeCell = (item: SegmentData): string => {
    const hasLink = Boolean(studyInstanceId && item.SOPInstanceUID);
    if (!hasLink) {
      return item.sliceRange ?? 'N/A';
    }

    const viewerUrl = `/viewer?StudyInstanceUIDs=${encodeURIComponent(
      studyInstanceId as string
    )}&initialSopInstanceUID=${encodeURIComponent(item.SOPInstanceUID as string)}`;

    return `
            <a
              href="${viewerUrl}"
              target="_blank"
              rel="noopener noreferrer"
              style="
                margin-left: 4px;
                font-size: 12px;
                color: #2563eb;
                text-decoration: underline;
                transition: color 0.15s ease, text-decoration-color 0.15s ease;
              "
              onmouseover="this.style.color='#1d4ed8'"
              onmouseout="this.style.color='#2563eb'"
            >
              ${item.sliceRange ?? 'N/A'}
            </a>
            `;
  };

  return `<hr>
  <p style="text-align: center; margin: 20px 0;">
      <strong style="font-size: 24px;">XYCAD-PAD VOLUMETRIC ASSESSMENT</strong>
  </p>
  <hr>
  ${upperKnee?.abdominalAorta ? `
<h2><strong style="font-size: 18px">ABDOMINAL AORTA:</strong></h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.abdominalAorta
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
  <br />
` : ''}
  ${upperKnee?.iliacR ? `
<h2><strong style="font-size: 18px">COMMON ILLIAC ARTERY (CIA) RIGHT:</strong></h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.iliacR
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
  <br />
` : ''}
  ${upperKnee?.iliacL ? `
<h2><strong style="font-size: 18px">COMMON ILLIAC ARTERY (CIA) LEFT:</strong></h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.iliacL
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
  <br />
` : ''}
  ${upperKnee?.eiliacR ? `
<h2><strong style="font-size: 18px">EXTERNAL ILLIAC ARTERY (EIA) RIGHT:</strong></h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.eiliacR
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
  <br />
` : ''}
  ${upperKnee?.eiliacL ? `
<h2>
    <strong><span style="font-size: 18px">EXTERNAL ILLIAC ARTERY (EIA) LEFT:</span></strong>
  </h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.eiliacL
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; text-align: center; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
  <br />
` : ''}
  ${upperKnee?.sfaR ? `
<h2><strong style="font-size: 18px">FEMORAL ARTERY (CFA/SFA not subdivided) RIGHT:</strong></h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.sfaR
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
  <br />
` : ''}
  ${upperKnee?.sfaL ? `
<h2><strong style="font-size: 18px">FEMORAL ARTERY (CFA/SFA not subdivided) LEFT:</strong></h2>

  <table style="border-collapse: collapse; width: 100%">
    <tbody>
      <tr style="border: 1px solid #bfccd4;">
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
        <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
        <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
      </tr>
      ${upperKnee?.sfaL
        ?.map((item, index) => {
          return `
          <tr key=${index} style="border: 1px solid #bfccd4;">
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
          <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
          <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
          </tr>
          `;
        })
        .join('')}
    </tbody>
  </table>
    <br />
` : ''}
    ${rightLeg?.poplitealR ? `
<h2><strong style="font-size: 18px">POPLITEAL ARTERY RIGHT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${rightLeg?.poplitealR
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}
      </tbody>
    </table>
    <br />
` : ''}
    ${leftLeg?.poplitealL ? `
<h2><strong style="font-size: 18px">POPLITEAL ARTERY LEFT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${leftLeg?.poplitealL
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}
      </tbody>
    </table>

    <br />
` : ''}
    ${rightLeg?.tibioR ? `
<h2><strong style="font-size: 18px">TIBIOPERONEA TRUNK (TPT) RIGHT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${rightLeg?.tibioR
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}
      </tbody>
    </table>
    <br />
` : ''}
    ${leftLeg?.tibioL ? `
<h2><strong style="font-size: 18px">TIBIOPERONEAL TRUNK (TPT) LEFT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${leftLeg?.tibioL
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}
      </tbody>
    </table>



    <br />
` : ''}

    ${rightLeg?.anteriorR ? `
<h2><strong style="font-size: 18px">ANTERIOR TIBIAL ARTERY (ATA) RIGHT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${rightLeg?.anteriorR
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}

      </tbody>
    </table>
    <br />
` : ''}
    ${leftLeg?.anteriorL ? `
<h2><strong style="font-size: 18px">ANTERIOR TIBIAL ARTERY (ATA) LEFT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${leftLeg?.anteriorL
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}

      </tbody>
    </table>
    <br />
` : ''}
    ${rightLeg?.posteriorR ? `
<h2><strong style="font-size: 18px">POSTERIOR TIBIAL ARTERY (PTA) RIGHT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${rightLeg?.posteriorR
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}

      </tbody>
    </table>
    <br />
` : ''}
    ${leftLeg?.posteriorL ? `
<h2><strong style="font-size: 18px">POSTERIOR TIBIAL ARTERY (PTA) LEFT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${leftLeg?.posteriorL
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}

      </tbody>
    </table>

    <br />
` : ''}
    ${rightLeg?.peronealR ? `
<h2><strong style="font-size: 18px">PERONEAL ARTERY RIGHT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${rightLeg?.peronealR
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}

      </tbody>
    </table>
    <br />
` : ''}
    ${leftLeg?.peronealL ? `
<h2><strong style="font-size: 18px">PERONEAL ARTERY LEFT:</strong></h2>

    <table style="border-collapse: collapse; width: 100%">
      <tbody>
        <tr style="border: 1px solid #bfccd4;">
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Segment</th>
          <th style="text-align: center;width: 20%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">XyCAD Assessment</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Length (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Diameter (mm)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Stenosis (%)</th>
          <th style="text-align: center;width: 16%; border: 1px solid #bfccd4; font-size: 13px; padding:0.2rem;">Slice Range(s)</th>
        </tr>
        ${leftLeg?.peronealL
        ?.map((item, index) => {
          return `
            <tr key=${index} style="border: 1px solid #bfccd4;">
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.segment ?? 'N/A'}</td>
            <td style="width: 20%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.aiAssessment ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.length ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.diameter ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${item.stenosis ?? 'N/A'}</td>
            <td style="width: 16%; border: 1px solid #bfccd4; text-align: center; font-size: 13px; padding:0.2rem;">${buildSliceRangeCell(item)}</td>
            </tr>
            `;
        })
        .join('')}
      </tbody>
    </table>
    <br>
` : ''}
    <p><strong style="font-size: 18px;">LEGEND:</strong></p>
<p>XyCAD Assessments are based on NASCET criteria:<br>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• Normal: Absence of the plaque and no stenosis</p>
<p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• Mild: Plaque with 20–49% stenosis</p>
<p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• Moderate: Plaque with 50–69% stenosis</p>
<p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• Severe: Plaque with 70–99% stenosis</p>
<p>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;• Occluded: Complete Occlusion</p>
<br>
    `;
};

export default getAiReportBody;
